/**
 * Formats a monetary amount using Intl.NumberFormat with the specified number of decimals.
 * Centralizes decimal formatting logic for all UI components (DRY).
 * @param {number} amount - The numeric amount to format
 * @param {number} decimals - Number of decimal places (from CMS numberOfDecimalsBE)
 * @returns {string} - Formatted number string (e.g., "12,345.99")
 */
export const formatAmountWithDecimals = (amount, decimals) => {
  const digits = decimals != null ? Number(decimals) : 0;
  return new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount || 0);
};

/**
 * Extrae y sanitiza `numberOfDecimalsBE` desde la respuesta del servicio
 * th-web-validations. El CMS devuelve la respuesta como un ARRAY de nodos
 * (ej. `[{ nid, ANCILLARIES: {...}, payments: {...} }]`), por lo que primero se
 * toma el primer elemento. El campo llega anidado dentro de `ANCILLARIES`.
 *
 * Devuelve `undefined` cuando la bandera NO llega (respuesta inválida/error o
 * campo ausente), para que los consumidores puedan omitirla y no propagarla a
 * los microservicios. Si llega con cualquier valor (0, 1, 2, N) se respeta.
 * @param {Object|Array} webValidations - Respuesta del servicio th-web-validations
 * @returns {number|undefined} - Número de decimales sanitizado, o undefined si no llega
 */
export function getNumberOfDecimalsBE(webValidations) {
  if (!webValidations || typeof webValidations !== 'object' || webValidations.error) {
    return undefined;
  }
  // El CMS regresa un array de nodos; se usa el primer elemento. Si llegara como
  // objeto plano (retrocompatibilidad), se usa tal cual.
  const node = Array.isArray(webValidations) ? webValidations[0] : webValidations;
  if (!node || typeof node !== 'object') {
    return undefined;
  }
  // El campo siempre vive dentro de ANCILLARIES.
  const raw = node.ANCILLARIES?.numberOfDecimalsBE;
  if (raw === null || raw === undefined || raw === '') {
    return undefined;
  }
  const num = Number(raw);
  return isNaN(num) ? undefined : num;
}

/**
 * @param {Array} storefronts - Storefront config from the CMS
 *
 * @return {Shape.storefrontConfig} storefrontConfig - Storefront config object
 */
export function normalizeStorefronts(storefronts) {
  const storefrontConfig = {};

  storefronts.forEach((s) => {
    const {
      storefront,
      storefront: {
        code,
        languageCode,
      },
    } = s;


    if (storefrontConfig[code]) {
      storefrontConfig[code][languageCode] = _sanitizeStorefrontData(storefront);
    } else {
      storefrontConfig[code] = {};
      storefrontConfig[code][languageCode] = _sanitizeStorefrontData(storefront);
    }
  });

  return storefrontConfig;
}

export function getErrorCodeLanguageBundle(requestUrl) {
  if (requestUrl.includes('booking')) {
    return '11110919';
  }
  if (requestUrl.includes('profile')) {
    return '15110906';
  }
  if (requestUrl.includes('checkin')) {
    return '12110908';
  }
  if (requestUrl.includes('myb')) {
    return '13110922';
  }
  if (requestUrl.includes('cyc')) {
    return '16110909';
  }
  if (requestUrl.includes('global')) {
    return '17110921';
  }
}


function _sanitizeStorefrontData({
  activeBeta = false,
  activePnrLookUpLight = false,
  bannerBaggage20KgMyb = false,
  bannersSectionsPay = '',
  bookingConfirmationPageAds = '',
  chatURL = '',
  chatURLBooking = '',
  chatURLCheckin = '',
  checkInConfirmationPageAds = '',
  code = '',
  contactNumber = '',
  contactUrl = '',
  cookie = '',
  cookieDesktop = '',
  cookieMobile = '',
  creationDate = '',
  currency = '',
  disabledITA = '',
  dutyFreeMYB = '',
  electronicVisaUrl = '',
  eSimUrl = '',
  externalURL = '',
  googleRichSnippets = '',
  isActiveCorporate = false,
  isBehaviourSecurity = '',
  identifier = '',
  interstitials = '',
  isEnableElectronicVisaMyb = false,
  isEnableEsimMYB = false,
  isExternal = '',
  isMobilityMadridEnableMYB = '',
  language = '',
  languageCode = '',
  languageName = '',
  linkBetaCC = '',
  loginRequired = false,
  manageTripDetailsPageAds = '',
  manageTripLandingPageAds = '',
  miniHelperChatURL = '',
  miniHelperNumber = '',
  mobilityMadridURL = '',
  name = '',
  newsletterURL = '',
  passwordResetProfile = '',
  popularDestinations = '',
  promoQueueId = '',
  recoverAccountProfile = '',
  schemaTags = '',
  seatdiscount = '',
  seatmapPriceInfoBox = '',
  showChatBot = '',
  showFriendsAndFamily = '',
  showSeatCP = '',
  staticLinks = '',
  storefrontId = '',
  storefrontTheme = '',
  termsAndConditions = '',
  testStorefront = '',
  travelerAdvisoryId = '',
  welcomeMYB = '',
  footerLegal = '',
  delayPercentage = '',
  fareOptionsColor = '',
  fareOptionsLabel = '',
  fareOptionsIcon = '',
  ffnassociationMYB = '',
  linkAppIos = '',
  linkAppAndroid = '',
  baggagePromo = '',
  exchangesBannerInCheckIn = '',
  upgradeVoucherRedemption = '',
  upgradeVoucherRedemptionMYB = '',
}) {
  return {
    activeBannerBaggage: bannerBaggage20KgMyb === 'true',
    activeBeta,
    activePnrLookUpLight: activePnrLookUpLight === 'true',
    bannersSectionsPay: typeof bannersSectionsPay === 'string' ? bannersSectionsPay : '',
    bookingConfirmationPageAds,
    chatURL,
    chatURLBooking,
    chatURLCheckin,
    checkInConfirmationPageAds,
    code,
    contactNumber,
    contactUrl,
    cookie: cookie === 'true',
    cookieDesktop,
    cookieMobile,
    creationDate,
    currency,
    disabledITA,
    dutyFreeMYB: dutyFreeMYB === 'true',
    electronicVisaUrl,
    eSimUrl,
    externalURL,
    googleRichSnippets,
    isActiveCorporate,
    isBehaviourSecurity: isBehaviourSecurity === 'true',
    identifier,
    interstitials: interstitials.length > 0 ? JSON.parse(interstitials) : interstitials,
    isEnableEsimMYB,
    isEnableElectronicVisaMyb,
    isExternal: isExternal === 'true',
    isMobilityMadridEnableMYB,
    language,
    languageCode,
    languageName,
    linkBetaCC,
    loginRequired: loginRequired === 'true',
    manageTripDetailsPageAds,
    manageTripLandingPageAds,
    miniHelperChatURL,
    miniHelperNumber,
    mobilityMadridURL,
    name,
    newsletterURL,
    passwordResetProfile: passwordResetProfile === 'true',
    popularDestinations,
    promoQueueId,
    recoverAccountProfile: recoverAccountProfile === 'true',
    schemaTags,
    seatdiscount: seatdiscount.length > 0 ? JSON.parse(seatdiscount) : seatdiscount,
    staticLinks: staticLinks.length > 0 ? JSON.parse(staticLinks) : staticLinks,
    seatmapPriceInfoBox,
    showChatBot,
    showFriendsAndFamily: showFriendsAndFamily === 'true',
    showSeatCP: showSeatCP === 'true',
    storefrontId,
    storefrontTheme,
    termsAndConditions,
    testStorefront,
    travelerAdvisoryId,
    welcomeMYB: welcomeMYB === 'true',
    footerLegal,
    delayPercentage,
    fareOptionsColor,
    fareOptionsLabel,
    fareOptionsIcon,
    ffnassociationMYB: ffnassociationMYB === 'true',
    linkAppIos,
    linkAppAndroid,
    baggagePromo,
    exchangesBannerInCheckIn,
    upgradeVoucherRedemption,
    upgradeVoucherRedemptionMYB: upgradeVoucherRedemptionMYB === 'true',
  };
}
